// src/modules/app-views/account/pages/Subscription/modals/VoipGotoAuthModal/index.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useMemo } from 'react';
import { PhoneOutlined } from '@ant-design/icons';
import GenericModal from 'components/core/GenericModal';
import appService from 'services/appService';
import axiosService from 'services/axiosService';
import axiosFetch from 'configs/fetchInterceptor';
import { API } from 'constants/Api';
import translateX from 'utils/translateX';
import gotoAuthStep from './Step1';

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const VoipGotoAuthModal = ({ show, setShow, afterClose, onSuccess }) => {
  // ---------------------------------------------------------------------------
  // 1. HANDLERS
  // ---------------------------------------------------------------------------
  const handleFinish = async ({ setLoading }) => {
    try {
      // 1. Build redirect_uri dynamically from current backend base URL
      // axiosFetch.defaults.baseURL = "http://localhost:8132/api" (varies by app/env)
      const backendBase = axiosFetch.defaults.baseURL || '';
      const redirectUri = `${backendBase}/v1/goto/callback`;

      // 2. Get authorize URL with popup mode and dynamic redirect_uri
      const [data] = await axiosService.get({
        url: `${API.GOTO.AUTHORIZE}?mode=popup&redirect_uri=${encodeURIComponent(redirectUri)}`,
        returnAsObject: true,
      });

      if (!data?.authorizeUrl) {
        await appService.message({ flag: 'e', text: 'goto_oauth_error' });
        return false;
      }

      // 2. Open popup window
      const popup = window.open(
        data.authorizeUrl,
        'goto-oauth',
        'width=600,height=700,scrollbars=yes,resizable=yes',
      );

      if (!popup || popup.closed) {
        await appService.message({ flag: 'w', text: 'goto_popup_blocked' });
        return false;
      }

      // 3. Keep modal open with loading while waiting for popup
      setLoading?.(true);

      // 4. Listen for postMessage from popup
      const result = await waitForPopupMessage(popup);

      if (result === 'connected') {
        onSuccess?.();
        return true;
      }

      // Error or timeout
      await appService.message({ flag: 'e', text: 'goto_oauth_error' });
      return false;
    } catch {
      await appService.message({ flag: 'e', text: 'goto_oauth_error' });
      return false;
    }
  };

  // ---------------------------------------------------------------------------
  // 2. LOCAL VARIABLES
  // ---------------------------------------------------------------------------
  const steps = useMemo(() => [gotoAuthStep()], []);

  // ---------------------------------------------------------------------------
  // 3. UI
  // ---------------------------------------------------------------------------
  return (
    <GenericModal
      show={show}
      setShow={setShow}
      icon={<PhoneOutlined />}
      title="goto_auth_title"
      steps={steps}
      onFinish={handleFinish}
      afterClose={afterClose}
      width={480}
      maskClosable={false}
      okText="goto_auth_button"
    />
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default VoipGotoAuthModal;

// *******************************************************************************
// HELPERS
// *******************************************************************************

/**
 * Wait for a postMessage from the OAuth popup window
 * Resolves with 'connected' or 'error', or rejects on timeout/close
 *
 * @param {Window} popup - The popup window reference
 * @param {number} timeoutMs - Maximum time to wait (default 5 minutes)
 * @returns {Promise<string>} The status from the popup
 */
function waitForPopupMessage(popup, timeoutMs = 5 * 60 * 1000) {
  return new Promise((resolve) => {
    let settled = false;

    const cleanup = () => {
      settled = true;
      window.removeEventListener('message', onMessage);
      clearInterval(pollTimer);
      clearTimeout(timeoutTimer);
    };

    // Listen for postMessage from popup
    const onMessage = (event) => {
      if (settled) return;
      if (event.data?.type !== 'goto-oauth') return;

      cleanup();
      resolve(event.data.status || 'error');
    };

    window.addEventListener('message', onMessage);

    // Poll to detect if popup was closed manually
    const pollTimer = setInterval(() => {
      if (settled) return;
      if (popup.closed) {
        cleanup();
        resolve('error');
      }
    }, 500);

    // Timeout safety
    const timeoutTimer = setTimeout(() => {
      if (settled) return;
      cleanup();
      if (!popup.closed) popup.close();
      resolve('error');
    }, timeoutMs);
  });
}
